import React, { useState, useEffect, useMemo } from 'react';
import FamilyCalendar from './FamilyCalendar';
import CalendarDashboard from './CalendarDashboard';
import { parseGedcomToTree } from '../utils/gedcomParser';
import './EnhancedComponents.css';

const Calendar = () => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [gedcomData, setGedcomData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadGedcomData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch('/data/rossetti.ged');
        
        if (response.ok) {
          const content = await response.text();
          
          const parsed = parseGedcomToTree(content);
          
          setGedcomData(parsed);
        } else {
          throw new Error(`Failed to fetch GEDCOM file: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Calendar: Error loading GEDCOM data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadGedcomData();
  }, []);

  const handleOpenCalendar = () => {
    setIsCalendarOpen(true);
  };

  const closeCalendar = () => {
    setIsCalendarOpen(false);
  };

  // Extract events for dashboard (similar to FamilyCalendar logic)
  const extractedEvents = useMemo(() => {
    if (!gedcomData) return [];
    
    // Helper function to get ordinal suffix
    const getOrdinalSuffix = (num) => {
      const j = num % 10;
      const k = num % 100;
      if (j === 1 && k !== 11) return 'st';
      if (j === 2 && k !== 12) return 'nd';
      if (j === 3 && k !== 13) return 'rd';
      return 'th';
    };
    
    const { individuals, families } = gedcomData;
    const events = [];

    // Parse date string to Date object
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      
      const patterns = [
        /(\d{1,2})\s+(\w+)\s+(\d{4})/,  // "15 MAR 1920"
        /(\w+)\s+(\d{1,2}),?\s+(\d{4})/, // "March 15, 1920"
        /(\d{4})-(\d{1,2})-(\d{1,2})/,   // "1920-03-15"
        /(\d{1,2})\/(\d{1,2})\/(\d{4})/,  // "03/15/1920"
        /(\d{4})/                         // "1920" (year only)
      ];

      const monthNames = {
        'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
        'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11,
        'JANUARY': 0, 'FEBRUARY': 1, 'MARCH': 2, 'APRIL': 3, 'JUNE': 5,
        'JULY': 6, 'AUGUST': 7, 'SEPTEMBER': 8, 'OCTOBER': 9, 'NOVEMBER': 10, 'DECEMBER': 11
      };

      for (let i = 0; i < patterns.length; i++) {
        const pattern = patterns[i];
        const match = dateStr.toUpperCase().match(pattern);
        if (match) {
          if (pattern === patterns[0]) { // "15 MAR 1920"
            const day = parseInt(match[1]);
            const month = monthNames[match[2]];
            const year = parseInt(match[3]);
            if (month !== undefined) {
              return new Date(year, month, day);
            }
          } else if (pattern === patterns[1]) { // "March 15, 1920"
            const month = monthNames[match[1].toUpperCase()];
            const day = parseInt(match[2]);
            const year = parseInt(match[3]);
            if (month !== undefined) {
              return new Date(year, month, day);
            }
          } else if (pattern === patterns[2]) { // "1920-03-15"
            return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
          } else if (pattern === patterns[3]) { // "03/15/1920"
            return new Date(parseInt(match[3]), parseInt(match[1]) - 1, parseInt(match[2]));
          } else if (pattern === patterns[4]) { // "1920"
            return new Date(parseInt(match[1]), 0, 1);
          }
        }
      }
      return null;
    };

    // Extract birth and death dates from individuals
    if (individuals) {
      Object.entries(individuals).forEach(([id, person]) => {
        if (person.birth) {
          const birthDate = parseDate(person.birth);
          if (birthDate) {
            events.push({
              id: `birth-${id}`,
              type: 'birth',
              date: birthDate,
              person: person.name,
              description: `${person.name} was born`,
              location: person.birthPlace || '',
              icon: '🎂'
            });
          }
        }

        if (person.death) {
          const deathDate = parseDate(person.death);
          if (deathDate) {
            events.push({
              id: `death-${id}`,
              type: 'death',
              date: deathDate,
              person: person.name,
              description: `${person.name} passed away`,
              location: person.deathPlace || '',
              icon: '🕊️'
            });
          }
        }
      });
    }

    // Extract marriage dates from families
    if (families) {
      Object.entries(families).forEach(([id, family]) => {
        if (family.marriage) {
          const marriageDate = parseDate(family.marriage);
          if (marriageDate) {
            const husband = individuals?.[family.husband]?.name || 'Unknown';
            const wife = individuals?.[family.wife]?.name || 'Unknown';
            events.push({
              id: `marriage-${id}`,
              type: 'marriage',
              date: marriageDate,
              person: `${husband} & ${wife}`,
              description: `${husband} married ${wife}`,
              location: family.marriagePlace || '',
              icon: '💒'
            });
          }
        }
      });
    }

    // Calculate anniversaries for the current year
    const currentYear = new Date().getFullYear();
    const anniversaries = [];

    events.forEach(event => {
      const originalYear = event.date.getFullYear();
      const yearsAgo = currentYear - originalYear;
      
      if (yearsAgo > 0) {
        const anniversaryDate = new Date(currentYear, event.date.getMonth(), event.date.getDate());
        
        let anniversaryDescription = '';
        let anniversaryIcon = '';
        
        if (event.type === 'birth') {
          anniversaryDescription = `${event.person}'s ${yearsAgo}${getOrdinalSuffix(yearsAgo)} birthday`;
          anniversaryIcon = '🎉';
        } else if (event.type === 'death') {
          anniversaryDescription = `${yearsAgo} years since ${event.person} passed away`;
          anniversaryIcon = '🌹';
        } else if (event.type === 'marriage') {
          anniversaryDescription = `${event.person}'s ${yearsAgo}${getOrdinalSuffix(yearsAgo)} wedding anniversary`;
          anniversaryIcon = '💕';
        }
        
        anniversaries.push({
          id: `anniversary-${event.id}`,
          type: `${event.type}-anniversary`,
          date: anniversaryDate,
          person: event.person,
          description: anniversaryDescription,
          location: event.location,
          icon: anniversaryIcon,
          yearsAgo: yearsAgo,
          originalDate: event.date
        });
      }
    });

    return [...events, ...anniversaries].sort((a, b) => a.date - b.date);
  }, [gedcomData]);

  return (
    <section id="calendar" className="calendar-section">
      <div className="container-xl">
        <div className="section-icon">
          <i className="fas fa-calendar-alt"></i>
        </div>
        
        <h2>Family Calendar</h2>
        
        <div className="calendar-intro">
          <p>
            Explore our family's important dates and milestones. Export events to your personal calendar, 
            see upcoming celebrations, and discover family history through our interactive calendar experience.
          </p>
        </div>

        {/* Loading and Error States */}
        {isLoading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading family calendar data...</p>
          </div>
        )}
        
        {error && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>Error loading family data: {error}</p>
            <div className="error-message">
              <p>Unable to load family data. Please check the console for details.</p>
            </div>
          </div>
        )}

        {/* Calendar Dashboard */}
        {gedcomData && !isLoading && !error && extractedEvents.length > 0 && (
          <CalendarDashboard events={extractedEvents} gedcomData={gedcomData} />
        )}

        {/* Full Calendar View Button */}
        {gedcomData && !isLoading && !error && (
          <div className="cta-buttons">
            <button 
              className="cta-btn primary" 
              onClick={handleOpenCalendar}
            >
              <i className="fas fa-calendar-alt"></i>
              Open Full Calendar View
            </button>
            <p className="cta-description">
              Access detailed calendar views with month, year, and timeline layouts
            </p>
          </div>
        )}
      </div>

      {isCalendarOpen && (
        <FamilyCalendar 
          gedcomData={gedcomData}
          onClose={closeCalendar}
        />
      )}
    </section>
  );
};

export default Calendar;
